/**
 * InProcessTransport — ServerTransport that runs entirely in-process.
 *
 * Used internally by {@link LocalRuntime}. Also exported for consumers building
 * custom integrations that need direct transport access without WebSocket overhead.
 *
 * Runner side (startAgentServer):
 *   onCommand(handler) — registers the runner's command handler
 *   send(event)        — delivers AgentEvents to onEvent subscribers
 *
 * Application side:
 *   inject(command)    — sends a command to the agent (deferred to avoid re-entrancy)
 *   onEvent(handler)   — subscribes to AgentEvents; returns unsubscribe fn
 *
 * @docLink packages/sdk/concepts#in-process-transport
 */

import type { AgentCommand, AgentEvent, ServerTransport } from "@skaile/workspaces/types";

export class InProcessTransport implements ServerTransport {
  private readonly commandHandlers = new Set<(cmd: AgentCommand) => void>();
  private readonly eventHandlers = new Set<(event: AgentEvent) => void>();
  // Commands injected before any handler is registered are buffered here and
  // flushed on the first onCommand() — without this they iterate an empty set
  // and are silently lost (e.g. a prompt sent during start()'s wiring window).
  private readonly pendingCommands: AgentCommand[] = [];

  // ── ServerTransport ───────────────────────────────────────────────────────

  // In-process: no socket to open or tear down — both are no-ops so serve()
  // can drive any transport's lifecycle without an instanceof check.
  async listen(): Promise<void> {}

  async close(): Promise<void> {}

  send(event: AgentEvent): void {
    for (const h of this.eventHandlers) h(event);
  }

  onCommand(handler: (cmd: AgentCommand) => void): () => void {
    const wasEmpty = this.commandHandlers.size === 0;
    this.commandHandlers.add(handler);
    if (wasEmpty && this.pendingCommands.length > 0) {
      const buffered = this.pendingCommands.splice(0);
      for (const cmd of buffered) {
        void Promise.resolve().then(() => handler(cmd));
      }
    }
    return () => this.commandHandlers.delete(handler);
  }

  onConnect(_handler: () => void): () => void {
    return () => {};
  }

  onDisconnect(_handler: () => void): () => void {
    return () => {};
  }

  /** True once a command handler is wired (i.e. the runner has started). */
  get connected(): boolean {
    return this.commandHandlers.size > 0;
  }

  // ── Application API ───────────────────────────────────────────────────────

  /**
   * Send a command into the agent runner. Deferred to avoid re-entrancy.
   * Commands injected before a handler is registered are buffered and
   * flushed when the runner wires its handler, so none are dropped.
   */
  inject(command: AgentCommand): void {
    if (this.commandHandlers.size === 0) {
      this.pendingCommands.push(command);
      return;
    }
    for (const h of this.commandHandlers) {
      void Promise.resolve().then(() => h(command));
    }
  }

  /** Subscribe to AgentEvents. Returns an unsubscribe function. */
  onEvent(handler: (event: AgentEvent) => void): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }
}
